import React, { useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { motion } from 'framer-motion';
import { 
  CheckCircle, XCircle, Trophy, ArrowLeft, RotateCcw, 
  ChevronRight, Lightbulb, ArrowRight, FileText, 
  TrendingUp, AlertCircle, BookOpen, ChevronDown, ChevronUp
} from 'lucide-react';

const WritingModelAnswer = ({ text, level = 'B2' }) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="bg-emerald-50 rounded-2xl border border-emerald-200 overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full p-5 flex items-center justify-between text-left"
      >
        <div className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-emerald-600" />
          <h3 className="font-bold text-emerald-800" style={{ fontFamily: 'Syne, sans-serif' }}>
            Version corrigee (modele {level})
          </h3>
        </div>
        {expanded ? <ChevronUp className="w-5 h-5 text-emerald-600" /> : <ChevronDown className="w-5 h-5 text-emerald-600" />}
      </button>
      {expanded && (
        <div className="px-5 pb-5 border-t border-emerald-200 pt-4">
          <p className="text-sm text-emerald-800 leading-relaxed whitespace-pre-wrap">{text}</p>
        </div>
      )}
    </div>
  );
};

const ExerciseResults = () => {
  const { attemptId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  
  const results = location.state?.results;
  const exercise = location.state?.exercise;
  
  if (!results) {
    navigate('/levels');
    return null;
  }

  const scorePercent = (results.score / results.total_points) * 100;

  return (
    <div className="min-h-screen bg-[#FDFCF8]">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-border shadow-sm">
        <div className="max-w-5xl mx-auto px-4 h-16 flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate(-2)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Module
          </Button>
          <h1 className="font-bold text-lg" style={{ fontFamily: 'Syne, sans-serif' }}>
            Results
          </h1>
          <div className="w-24"></div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8">
        {/* Score Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className={`rounded-3xl p-8 mb-8 text-white ${
            results.passed 
              ? 'bg-gradient-to-br from-green-500 to-green-600' 
              : 'bg-gradient-to-br from-orange-500 to-red-500'
          }`}
        >
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-6">
              <div className="w-20 h-20 rounded-2xl bg-white/20 flex items-center justify-center">
                {results.passed ? (
                  <Trophy className="w-10 h-10" />
                ) : (
                  <RotateCcw className="w-10 h-10" />
                )}
              </div>
              <div>
                <h2 className="text-3xl font-bold mb-1" style={{ fontFamily: 'Syne, sans-serif' }}>
                  {results.score}/{results.total_points}
                </h2>
                <p className="text-white/80 text-lg">
                  {results.passed 
                    ? 'Congratulations! You passed!' 
                    : 'Keep practicing! You need 80% to pass.'}
                </p>
              </div>
            </div>
            
            <div className="text-center md:text-right">
              <div className="text-5xl font-bold mb-1">{Math.round(scorePercent)}%</div>
              <Progress value={scorePercent} className="h-3 w-48 bg-white/20" />
            </div>
          </div>
        </motion.div>

        {/* Next Test Unlocked */}
        {results.next_unlocked && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-green-50 rounded-xl p-4 mb-6 border border-green-200 flex items-center gap-4"
          >
            <CheckCircle className="w-6 h-6 text-green-600" />
            <div className="flex-1">
              <p className="font-medium text-green-800">Next exercise unlocked!</p>
              <p className="text-sm text-green-600">You can now continue to the next exercise.</p>
            </div>
            <ArrowRight className="w-5 h-5 text-green-600" />
          </motion.div>
        )}

        {/* Corrections */}
        {results.corrections?.length > 0 && results.corrections[0]?.question_id && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-2xl border border-border/50 overflow-hidden mb-8"
          >
            <div className="p-4 border-b border-border/50">
              <h3 className="font-bold" style={{ fontFamily: 'Syne, sans-serif' }}>
                Answer Review
              </h3>
            </div>
            <div className="divide-y divide-border/50">
              {results.corrections.map((correction, index) => (
                <div
                  key={index}
                  className={`p-4 ${correction.is_correct ? 'bg-green-50/50' : 'bg-red-50/50'}`}
                >
                  <div className="flex items-start gap-3">
                    {correction.is_correct ? (
                      <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-600 mt-0.5" />
                    )}
                    <div className="flex-1">
                      <p className="font-medium mb-2">Question {index + 1}</p>
                      <div className="grid md:grid-cols-2 gap-2 text-sm mb-2">
                        <div className="p-2 rounded bg-white">
                          <span className="text-muted-foreground">Your answer: </span>
                          <span className={correction.is_correct ? 'text-green-700' : 'text-red-700'}>
                            {correction.user_answer || 'No answer'}
                          </span>
                        </div>
                        <div className="p-2 rounded bg-white">
                          <span className="text-muted-foreground">Correct: </span>
                          <span className="text-green-700 font-medium">{correction.correct_answer}</span>
                        </div>
                      </div>
                      {correction.explanation && (
                        <div className="flex items-start gap-2 p-2 bg-white rounded text-sm">
                          <Lightbulb className="w-4 h-4 text-primary mt-0.5" />
                          <div>
                            <p>{correction.explanation}</p>
                            {correction.explanation_fr && (
                              <p className="text-muted-foreground mt-1">{correction.explanation_fr}</p>
                            )}
                          </div>
                        </div>
                      )}
                      {correction.strategy_tip && (
                        <div className="flex items-start gap-2 p-2 bg-blue-50 rounded text-sm mt-1">
                          <Lightbulb className="w-4 h-4 text-blue-500 mt-0.5" />
                          <div>
                            <p className="text-blue-800">{correction.strategy_tip}</p>
                            {correction.strategy_tip_fr && (
                              <p className="text-blue-600 mt-1">{correction.strategy_tip_fr}</p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Speaking Evaluation */}
        {results.corrections?.length > 0 && results.corrections[0]?.type === 'speaking_evaluation' && (() => {
          const correction = results.corrections[0];
          const evaluation = correction.evaluation || {};
          const scores = evaluation.scores || {};
          const criteria = correction.criteria || [];
          const hasStructuredScores = Object.keys(scores).length > 0;

          return (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="space-y-6 mb-8"
            >
              {/* Transcript */}
              {(correction.transcript || evaluation.transcript) && (
                <div className="bg-white rounded-2xl p-5 border border-border/50">
                  <div className="flex items-center gap-2 mb-3">
                    <FileText className="w-5 h-5 text-muted-foreground" />
                    <h3 className="font-bold" style={{ fontFamily: 'Syne, sans-serif' }}>Votre transcription</h3>
                  </div>
                  <p className="text-sm leading-relaxed whitespace-pre-wrap text-foreground bg-muted/30 p-4 rounded-xl">
                    {correction.transcript || evaluation.transcript}
                  </p>
                </div>
              )}

              {/* Criteria Scores */}
              {hasStructuredScores && (
                <div className="bg-white rounded-2xl border border-border/50 overflow-hidden">
                  <div className="p-4 border-b border-border/50">
                    <h3 className="font-bold" style={{ fontFamily: 'Syne, sans-serif' }}>
                      Grille d'evaluation DELF {exercise?.level || 'B2'} — Production Orale
                    </h3>
                  </div>
                  <div className="p-4 space-y-3">
                    {criteria.map((c, i) => {
                      const criterionScore = scores[c.code] ?? 0;
                      const pct = (criterionScore / c.max_points) * 100;
                      return (
                        <div key={i} className="space-y-1">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium">{c.name_fr}</span>
                            <span className={`text-sm font-bold ${pct >= 60 ? 'text-green-600' : pct >= 40 ? 'text-amber-600' : 'text-red-600'}`}>
                              {criterionScore}/{c.max_points}
                            </span>
                          </div>
                          <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full transition-all duration-500 ${pct >= 60 ? 'bg-green-500' : pct >= 40 ? 'bg-amber-500' : 'bg-red-500'}`}
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                          <p className="text-xs text-muted-foreground">{c.description_fr}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Strengths */}
              {evaluation.strengths_fr?.length > 0 && (
                <div className="bg-green-50 rounded-2xl p-5 border border-green-200">
                  <div className="flex items-center gap-2 mb-3">
                    <TrendingUp className="w-5 h-5 text-green-600" />
                    <h3 className="font-bold text-green-800" style={{ fontFamily: 'Syne, sans-serif' }}>Points forts</h3>
                  </div>
                  <ul className="space-y-1.5">
                    {evaluation.strengths_fr.map((s, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-green-700">
                        <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        <span>{s}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Weaknesses */}
              {evaluation.weaknesses_fr?.length > 0 && (
                <div className="bg-red-50 rounded-2xl p-5 border border-red-200">
                  <div className="flex items-center gap-2 mb-3">
                    <AlertCircle className="w-5 h-5 text-red-600" />
                    <h3 className="font-bold text-red-800" style={{ fontFamily: 'Syne, sans-serif' }}>Points faibles</h3>
                  </div>
                  <ul className="space-y-1.5">
                    {evaluation.weaknesses_fr.map((w, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-red-700">
                        <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        <span>{w}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Model Answer */}
              {evaluation.model_answer && (
                <WritingModelAnswer text={evaluation.model_answer} level={exercise?.level} />
              )}

              {/* Speaking Tips */}
              {evaluation.speaking_tips_fr?.length > 0 && (
                <div className="bg-purple-50 rounded-2xl p-5 border border-purple-200">
                  <div className="flex items-center gap-2 mb-3">
                    <BookOpen className="w-5 h-5 text-purple-600" />
                    <h3 className="font-bold text-purple-800" style={{ fontFamily: 'Syne, sans-serif' }}>Conseils pour l'oral</h3>
                  </div>
                  <ul className="space-y-1.5">
                    {evaluation.speaking_tips_fr.map((t, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-purple-700">
                        <Lightbulb className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        <span>{t}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Raw fallback */}
              {evaluation.feedback_raw && !hasStructuredScores && (
                <div className="bg-white rounded-2xl p-6 border border-border/50">
                  <div className="flex items-center gap-2 mb-4">
                    <Lightbulb className="w-5 h-5 text-primary" />
                    <h3 className="font-bold" style={{ fontFamily: 'Syne, sans-serif' }}>AI Feedback</h3>
                  </div>
                  <p className="whitespace-pre-wrap text-sm">{evaluation.feedback_raw}</p>
                </div>
              )}
            </motion.div>
          );
        })()}

        {/* Writing Evaluation (Structured DELF B2 Grading) */}
        {results.corrections?.length > 0 && results.corrections[0]?.type === 'writing_evaluation' && (() => {
          const correction = results.corrections[0];
          const evaluation = correction.evaluation || {};
          const scores = evaluation.scores || {};
          const criteria = correction.criteria || [];
          const hasStructuredScores = Object.keys(scores).length > 0;

          return (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="space-y-6 mb-8"
            >
              {/* Word Count */}
              {correction.word_count !== undefined && (
                <div className="bg-white rounded-2xl p-4 border border-border/50 flex items-center gap-3">
                  <FileText className="w-5 h-5 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Nombre de mots :</span>
                  <span className="font-bold">{correction.word_count}</span>
                </div>
              )}

              {/* Criteria Scores */}
              {hasStructuredScores && (
                <div className="bg-white rounded-2xl border border-border/50 overflow-hidden">
                  <div className="p-4 border-b border-border/50">
                    <h3 className="font-bold" style={{ fontFamily: 'Syne, sans-serif' }}>
                      Grille d'evaluation DELF {exercise?.level || 'B2'}
                    </h3>
                  </div>
                  <div className="p-4 space-y-3">
                    {criteria.map((c, i) => {
                      const criterionScore = scores[c.code] ?? 0;
                      const pct = (criterionScore / c.max_points) * 100;
                      return (
                        <div key={i} className="space-y-1">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium">{c.name_fr}</span>
                            <span className={`text-sm font-bold ${pct >= 60 ? 'text-green-600' : pct >= 40 ? 'text-amber-600' : 'text-red-600'}`}>
                              {criterionScore}/{c.max_points}
                            </span>
                          </div>
                          <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full transition-all duration-500 ${pct >= 60 ? 'bg-green-500' : pct >= 40 ? 'bg-amber-500' : 'bg-red-500'}`}
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                          <p className="text-xs text-muted-foreground">{c.description_fr}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Strengths */}
              {evaluation.strengths_fr?.length > 0 && (
                <div className="bg-green-50 rounded-2xl p-5 border border-green-200">
                  <div className="flex items-center gap-2 mb-3">
                    <TrendingUp className="w-5 h-5 text-green-600" />
                    <h3 className="font-bold text-green-800" style={{ fontFamily: 'Syne, sans-serif' }}>Points forts</h3>
                  </div>
                  <ul className="space-y-1.5">
                    {evaluation.strengths_fr.map((s, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-green-700">
                        <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        <span>{s}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Weaknesses */}
              {evaluation.weaknesses_fr?.length > 0 && (
                <div className="bg-red-50 rounded-2xl p-5 border border-red-200">
                  <div className="flex items-center gap-2 mb-3">
                    <AlertCircle className="w-5 h-5 text-red-600" />
                    <h3 className="font-bold text-red-800" style={{ fontFamily: 'Syne, sans-serif' }}>Points faibles</h3>
                  </div>
                  <ul className="space-y-1.5">
                    {evaluation.weaknesses_fr.map((w, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-red-700">
                        <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        <span>{w}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Improvements */}
              {evaluation.improvements_fr?.length > 0 && (
                <div className="bg-blue-50 rounded-2xl p-5 border border-blue-200">
                  <div className="flex items-center gap-2 mb-3">
                    <Lightbulb className="w-5 h-5 text-blue-600" />
                    <h3 className="font-bold text-blue-800" style={{ fontFamily: 'Syne, sans-serif' }}>Ameliorations suggerees</h3>
                  </div>
                  <ul className="space-y-1.5">
                    {evaluation.improvements_fr.map((imp, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-blue-700">
                        <ArrowRight className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        <span>{imp}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Corrected Version */}
              {evaluation.corrected_version && (
                <WritingModelAnswer text={evaluation.corrected_version} level={exercise?.level} />
              )}

              {/* Strategy Tips */}
              {evaluation.strategy_tips_fr?.length > 0 && (
                <div className="bg-purple-50 rounded-2xl p-5 border border-purple-200">
                  <div className="flex items-center gap-2 mb-3">
                    <BookOpen className="w-5 h-5 text-purple-600" />
                    <h3 className="font-bold text-purple-800" style={{ fontFamily: 'Syne, sans-serif' }}>Conseils strategiques</h3>
                  </div>
                  <ul className="space-y-1.5">
                    {evaluation.strategy_tips_fr.map((t, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-purple-700">
                        <Lightbulb className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        <span>{t}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Raw fallback */}
              {evaluation.feedback_raw && !hasStructuredScores && (
                <div className="bg-white rounded-2xl p-6 border border-border/50">
                  <div className="flex items-center gap-2 mb-4">
                    <Lightbulb className="w-5 h-5 text-primary" />
                    <h3 className="font-bold" style={{ fontFamily: 'Syne, sans-serif' }}>AI Feedback</h3>
                  </div>
                  <div className="prose prose-sm max-w-none">
                    <p className="whitespace-pre-wrap">{evaluation.feedback_raw}</p>
                  </div>
                </div>
              )}
            </motion.div>
          );
        })()}

        {/* Legacy Writing Feedback (for old data) */}
        {results.corrections?.length > 0 && results.corrections[0]?.feedback && !results.corrections[0]?.type && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-2xl p-6 border border-border/50 mb-8"
          >
            <div className="flex items-center gap-2 mb-4">
              <Lightbulb className="w-5 h-5 text-primary" />
              <h3 className="font-bold" style={{ fontFamily: 'Syne, sans-serif' }}>AI Feedback</h3>
            </div>
            <div className="prose prose-sm max-w-none">
              <p className="whitespace-pre-wrap">{results.corrections[0].feedback}</p>
            </div>
          </motion.div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4">
          <Button
            variant="outline"
            className="flex-1 rounded-full"
            onClick={() => navigate(-2)}
          >
            Back to Module
          </Button>
          {results.passed && results.next_unlocked ? (
            <Button
              className="flex-1 rounded-full font-bold"
              onClick={() => navigate(-2)}
            >
              Continue to Next Exercise
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button
              className="flex-1 rounded-full font-bold"
              onClick={() => navigate(-1)}
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Retry Exercise
            </Button>
          )}
        </div>
      </main>
    </div>
  );
};

export default ExerciseResults;
